import { useCallback } from 'react';
import { Node, Edge } from 'reactflow';
import { flowToJson, validateFlow as validateFlowUtil } from '../utils/flowToJson';
import { jsonToFlow, validateJsonFormat } from '../utils/jsonToFlow';
import { validateFlow as validateFlowStructure } from '../utils/validation';

interface IJsonExportHookProps {
    nodes: Node[];
    edges: Edge[];
    nodeConfigs: Record<string, any>;
    questions: any[];
    assessmentUuid: string;
}

interface IUseJsonExportReturn {
    generateJson: () => any;
    exportJson: () => void;
    importJson: (jsonData: any) => void;
    validateFlow: () => { isValid: boolean; errors: string[]; warnings?: string[] };
}

export const useJsonExport = ({
    nodes,
    edges,
    nodeConfigs,
    questions,
    assessmentUuid,
}: IJsonExportHookProps): IUseJsonExportReturn => {
    
    const generateJson = useCallback(() => {
        return flowToJson(nodes, edges, {
            assessmentUuid,
            questions,
            nodeConfigs,
        });
    }, [nodes, edges, nodeConfigs, questions, assessmentUuid]);

    const exportJson = useCallback(() => {
        const jsonConfig = generateJson();
        const dataStr = JSON.stringify(jsonConfig, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `rules_${Date.now()}.json`;
        link.click();
        URL.revokeObjectURL(url);
    }, [generateJson]);

    const importJson = useCallback((jsonData: any) => {
        // 验证JSON格式
        const validation = validateJsonFormat(jsonData);
        if (!validation.isValid) {
            console.error('Invalid JSON format:', validation.errors);
            return null;
        }

        // 转换为流程图数据
        return jsonToFlow(jsonData);
    }, []);

    const validateFlow = useCallback(() => {
        return validateFlowStructure(nodes, edges, nodeConfigs);
    }, [nodes, edges, nodeConfigs]);

    return {
        generateJson,
        exportJson,
        importJson,
        validateFlow,
    };
};
